/* eslint-disable max-lines */
import type {AnyAction} from 'redux';

import {batchActions} from 'utils/batch_actions';

import {ChannelTypes, PreferenceTypes, UserTypes} from 'mattermost-redux/action_types';

import {Client4} from 'mattermost-redux/client';

import {General, Preferences} from '../constants';
import {CategoryTypes} from 'mattermost-redux/constants/channel_categories';
import {MarkUnread} from 'mattermost-redux/constants/channels';

import {getCategoryInTeamByType} from 'mattermost-redux/selectors/entities/channel_categories';
import {
    getChannel as getChannelSelector,
    getChannelsNameMapInTeam,
    getMyChannelMember as getMyChannelMemberSelector,
    getRedirectChannelNameForTeam,
    isManuallyUnread,
} from 'mattermost-redux/selectors/entities/channels';
import {getConfig} from 'mattermost-redux/selectors/entities/general';
import {getCurrentTeamId, getTeamById} from 'mattermost-redux/selectors/entities/teams';

import type {
    ActionFunc,
    ActionResult,
    DispatchFunc,
    GetStateFunc,
    ThunkActionWithResult,
} from 'mattermost-redux/types/actions';

import type {
    Channel,
    ChannelNotifyProps,
    ChannelMembership,
    ChannelModerationPatch,
    ChannelsWithTotalCount,
    ChannelSearchOpts,
} from 'mattermost-redux/types/channels';

import type {PreferenceType} from 'mattermost-redux/types/preferences';

import * as Utils from 'mattermost-redux/utils/channel_utils';

import type {Client4Error, Options} from '@mattermost/types/client4';

import {addChannelToInitialCategory, addChannelToChannelCategory} from 'features/sidebar';

import {selectChannel} from 'features/channels/actions/select_channel';

import {readOnlyChannelsSlice} from '../../../../features/channels';

import {fetchUsersByIdsDebounced} from 'features/users/actions/fetch_users_by_ids';

import {getBool} from 'mattermost-redux/selectors/entities/preferences';
import {retry} from 'utils/retry';

import type {Team} from '@mattermost/types/teams';

import {ServerErrorIds} from 'utils/constants';
import {ClientError} from 'mattermost-redux/client/client4';
import {openInsufficientPermissionsModal} from 'actions/views/open_insufficient_permissions_modal';

import {fetchMyCategories} from './channel_categories';
import {bindClientFunc, forceLogoutIfNecessary} from './helpers';
import {savePreferences} from './preferences';
import {loadRolesIfNeeded} from './roles';

export function createChannel(channel: Channel, userId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let created;
        try {
            created = await Client4.createChannel(channel);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({
                type: ChannelTypes.CREATE_CHANNEL_FAILURE,
                error,
            });
            return {error};
        }

        const member = {
            channel_id: created.id,
            user_id: userId,
            roles: `${General.CHANNEL_USER_ROLE} ${General.CHANNEL_ADMIN_ROLE}`,
            last_viewed_at: 0,
            msg_count: 0,
            mention_count: 0,
            notify_props: {desktop: 'default', mark_unread: 'all'},
            last_update_at: created.create_at,
        };

        const actions: AnyAction[] = [];
        const {channels, myMembers} = getState().entities.channels;

        if (!channels[created.id]) {
            actions.push({type: ChannelTypes.RECEIVED_CHANNEL, data: created});
        }

        if (!myMembers[created.id]) {
            actions.push({type: ChannelTypes.RECEIVED_MY_CHANNEL_MEMBER, data: member});
            dispatch(loadRolesIfNeeded(member.roles.split(' ')));
        }

        dispatch(
            batchActions([
                ...actions,
                {
                    type: ChannelTypes.CREATE_CHANNEL_SUCCESS,
                },
            ]),
        );

        dispatch(addChannelToInitialCategory({channel: created, setOnServer: true}));

        return {data: created};
    };
}

export function createDirectChannel(userId: string, otherUserId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.CREATE_CHANNEL_REQUEST, data: null});

        let created;
        try {
            created = await Client4.createDirectChannel([userId, otherUserId]);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.CREATE_CHANNEL_FAILURE, error});
            return {error};
        }

        const member = {
            channel_id: created.id,
            user_id: userId,
            roles: `${General.CHANNEL_USER_ROLE}`,
            last_viewed_at: 0,
            msg_count: 0,
            mention_count: 0,
            notify_props: {desktop: 'default', mark_unread: 'all'},
            last_update_at: created.create_at,
        };

        const preferences = [
            {user_id: userId, category: Preferences.CATEGORY_DIRECT_CHANNEL_SHOW, name: otherUserId, value: 'true'},
            {
                user_id: userId,
                category: Preferences.CATEGORY_CHANNEL_OPEN_TIME,
                name: created.id,
                value: new Date().getTime().toString(),
            },
        ];

        dispatch(savePreferences(userId, preferences));

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNEL,
                    data: created,
                },
                {
                    type: ChannelTypes.RECEIVED_MY_CHANNEL_MEMBER,
                    data: member,
                },
                {
                    type: PreferenceTypes.RECEIVED_PREFERENCES,
                    data: preferences,
                },
                {
                    type: ChannelTypes.CREATE_CHANNEL_SUCCESS,
                },
                {
                    type: UserTypes.RECEIVED_PROFILES_LIST_IN_CHANNEL,
                    id: created.id,
                    data: [{id: userId}, {id: otherUserId}],
                },
            ]),
        );

        dispatch(addChannelToInitialCategory({channel: created, setOnServer: false}));

        dispatch(loadRolesIfNeeded(member.roles.split(' ')));

        return {data: created};
    };
}

export function markGroupChannelOpen(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const {currentUserId} = state.entities.users;
        const isGroupChannelShowPreferenceTrusty = getBool(state, Preferences.CATEGORY_GROUP_CHANNEL_SHOW, channelId);

        if (isGroupChannelShowPreferenceTrusty) {
            return {data: true};
        }

        const preferences: PreferenceType[] = [
            {user_id: currentUserId, category: Preferences.CATEGORY_GROUP_CHANNEL_SHOW, name: channelId, value: 'true'},
            {
                user_id: currentUserId,
                category: Preferences.CATEGORY_CHANNEL_OPEN_TIME,
                name: channelId,
                value: new Date().getTime().toString(),
            },
        ];

        return dispatch(savePreferences(currentUserId, preferences));
    };
}

export function createGroupChannel(userIds: string[]): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.CREATE_CHANNEL_REQUEST, data: null});

        const {currentUserId} = getState().entities.users;

        let created;
        try {
            created = await Client4.createGroupChannel(userIds);
        } catch (error) {
            if (error instanceof ClientError && error.server_error_id === ServerErrorIds.INSUFFICIENT_PERMISSIONS) {
                dispatch(openInsufficientPermissionsModal('CREATE_GM_CHANNEL'));
            } else {
                forceLogoutIfNecessary(error, dispatch, getState);
            }
            dispatch({type: ChannelTypes.CREATE_CHANNEL_FAILURE, error});
            return {error};
        }

        let member: Partial<ChannelMembership> | undefined = {
            channel_id: created.id,
            user_id: currentUserId,
            roles: `${General.CHANNEL_USER_ROLE}`,
            last_viewed_at: 0,
            msg_count: 0,
            mention_count: 0,
            msg_count_root: 0,
            mention_count_root: 0,
            notify_props: {desktop: 'default', mark_unread: 'all'},
            last_update_at: created.create_at,
        };

        // Check the channel previous existency: if the channel already have
        // posts is because it existed before.
        if (created.total_msg_count > 0) {
            const storeMember = getMyChannelMemberSelector(getState(), created.id);
            if (storeMember === null) {
                try {
                    member = await Client4.getMyChannelMember(created.id);
                } catch {
                    // do nothing
                }
            } else {
                member = storeMember;
            }
        }

        dispatch(markGroupChannelOpen(created.id));

        const profilesInChannel = userIds.map((id) => ({id}));
        profilesInChannel.push({id: currentUserId}); // currentUserId is optionally in userIds, but the reducer will get rid of a duplicate

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNEL,
                    data: created,
                },
                {
                    type: ChannelTypes.RECEIVED_MY_CHANNEL_MEMBER,
                    data: member,
                },
                {
                    type: ChannelTypes.CREATE_CHANNEL_SUCCESS,
                },
                {
                    type: UserTypes.RECEIVED_PROFILES_LIST_IN_CHANNEL,
                    id: created.id,
                    data: profilesInChannel,
                },
            ]),
        );

        dispatch(addChannelToInitialCategory({channel: created, setOnServer: false}));

        dispatch(loadRolesIfNeeded((member && member.roles && member.roles.split(' ')) || []));

        return {data: created};
    };
}

export function patchChannel(channelId: string, patch: Partial<Channel>): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.UPDATE_CHANNEL_REQUEST, data: null});

        let updated;
        try {
            updated = await Client4.patchChannel(channelId, patch);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);

            dispatch({type: ChannelTypes.UPDATE_CHANNEL_FAILURE, error});
            return {error};
        }
        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNEL,
                    data: updated,
                },
                {
                    type: ChannelTypes.UPDATE_CHANNEL_SUCCESS,
                },
            ]),
        );

        return {data: updated};
    };
}

export function updateChannel(channel: Channel): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.UPDATE_CHANNEL_REQUEST, data: null});

        let updated;
        try {
            updated = await Client4.updateChannel(channel);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);

            dispatch({type: ChannelTypes.UPDATE_CHANNEL_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNEL,
                    data: updated,
                },
                {
                    type: ChannelTypes.UPDATE_CHANNEL_SUCCESS,
                },
            ]),
        );

        return {data: updated};
    };
}

export function transformGroupToChannel(
    channelId: string,
    teamId: string,
    channelName: string,
    displayName: string,
): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.UPDATE_CHANNEL_REQUEST, data: null});

        let channel;
        try {
            channel = await Client4.transformGroupToChannel(channelId, teamId, channelName, displayName);
        } catch (error: unknown) {
            forceLogoutIfNecessary(error, dispatch, getState);

            dispatch({type: ChannelTypes.UPDATE_CHANNEL_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNEL,
                    data: channel,
                },
                {
                    type: ChannelTypes.UPDATE_CHANNEL_SUCCESS,
                },
            ]),
        );
        dispatch(fetchMyCategories(teamId));

        return {data: true};
    };
}

export function updateChannelPrivacy(channelId: string, privacy: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.UPDATE_CHANNEL_REQUEST, data: null});

        let updatedChannel;
        try {
            updatedChannel = await Client4.updateChannelPrivacy(channelId, privacy);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);

            dispatch({type: ChannelTypes.UPDATE_CHANNEL_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNEL,
                    data: updatedChannel,
                },
                {
                    type: ChannelTypes.UPDATE_CHANNEL_SUCCESS,
                },
            ]),
        );

        return {data: updatedChannel};
    };
}

export function updateChannelNotifyProps(userId: string, channelId: string, props: ChannelNotifyProps): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const notifyProps = {
            user_id: userId,
            channel_id: channelId,
            ...props,
        };

        try {
            await Client4.updateChannelNotifyProps(notifyProps);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);

            return {error};
        }

        const member = getState().entities.channels.myMembers[channelId] || {};
        const currentNotifyProps = member.notify_props || {};

        dispatch({
            type: ChannelTypes.RECEIVED_CHANNEL_PROPS,
            data: {
                channel_id: channelId,
                notifyProps: {...currentNotifyProps, ...notifyProps},
            },
        });

        return {data: true};
    };
}

export function getChannelByName(teamId: string, channelName: string, includeDeleted = false): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let data;
        try {
            data = await Client4.getChannelByName(teamId, channelName, includeDeleted);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch({
            type: ChannelTypes.RECEIVED_CHANNEL,
            data,
        });

        return {data};
    };
}

export function getChannelByNameAndTeamName(teamName: string, channelName: string, includeDeleted = false): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let data;
        try {
            data = await Client4.getChannelByNameAndTeamName(teamName, channelName, includeDeleted);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch({
            type: ChannelTypes.RECEIVED_CHANNEL,
            data,
        });

        return {data};
    };
}

export function getChannel(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let data;
        try {
            data = await Client4.getChannel(channelId);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch({
            type: ChannelTypes.RECEIVED_CHANNEL,
            data,
        });

        return {data};
    };
}

export function getChannelAndMyMember(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let channel;
        let member;
        try {
            const [channelRequestResult, memberRequestResult] = await Promise.all([
                Client4.getChannel(channelId),
                Client4.getMyChannelMember(channelId),
            ]);

            channel = channelRequestResult;
            member = memberRequestResult;
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNEL,
                    data: channel,
                },
                {
                    type: ChannelTypes.RECEIVED_MY_CHANNEL_MEMBER,
                    data: member,
                },
            ]),
        );
        dispatch(loadRolesIfNeeded(member.roles.split(' ')));

        return {data: {channel, member}};
    };
}

export function getChannelTimezones(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let channelTimezones;
        try {
            channelTimezones = await Client4.getChannelTimezones(channelId);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        return {data: channelTimezones};
    };
}

export function fetchMyChannelsAndMembers(teamId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({
            type: ChannelTypes.CHANNELS_REQUEST,
            data: null,
        });

        let channels;
        let channelMembers;
        const state = getState();

        try {
            const [channelsResult, channelMembersResult] = await Promise.all([
                retry(() => Client4.getMyChannels(teamId, true), {count: 3, delay: 1000}),
                retry(() => Client4.getMyChannelMembers(teamId), {count: 3, delay: 1000}),
            ]);

            channels = channelsResult;
            channelMembers = channelMembersResult;
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.CHANNELS_FAILURE, error});
            return {error};
        }

        const {currentUserId} = state.entities.users;
        const {currentChannelId} = state.entities.channels;

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNELS,
                    teamId,
                    data: channels,
                    currentChannelId,
                },
                {
                    type: ChannelTypes.CHANNELS_SUCCESS,
                },
                {
                    type: ChannelTypes.RECEIVED_MY_CHANNEL_MEMBERS,
                    data: channelMembers,
                    sync: false,
                    channels,
                    remove: Utils.getChannelsIdForTeam(state, teamId),
                    currentUserId,
                    currentChannelId,
                },
            ]),
        );
        const roles = new Set<string>();
        for (const member of channelMembers) {
            for (const role of member.roles.split(' ')) {
                roles.add(role);
            }
        }
        if (roles.size > 0) {
            dispatch(loadRolesIfNeeded(roles));
        }

        return {data: {channels, members: channelMembers}};
    };
}

export function fetchAllMyTeamsChannelsAndChannelMembers(): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const {currentUserId} = state.entities.users;
        let channels;
        let channelsMembers;
        try {
            const [channelsResult, channelsMembersResult] = await Promise.all([
                Client4.getAllTeamsChannels(),
                Client4.getAllChannelsMembers(currentUserId),
            ]);

            channels = channelsResult;
            channelsMembers = channelsMembersResult;
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_ALL_CHANNELS,
                    data: channels,
                },
                {
                    type: ChannelTypes.RECEIVED_MY_CHANNEL_MEMBERS,
                    data: channelsMembers,
                    currentUserId,
                },
            ]),
        );
        return {data: {channels, channelsMembers}};
    };
}

export function getMyChannelMembers(teamId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let channelMembers;
        try {
            channelMembers = await Client4.getMyChannelMembers(teamId);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        const state = getState();
        const {currentUserId} = state.entities.users;
        const {currentChannelId} = state.entities.channels;

        dispatch({
            type: ChannelTypes.RECEIVED_MY_CHANNEL_MEMBERS,
            data: channelMembers,
            remove: Utils.getChannelsIdForTeam(state, teamId),
            currentUserId,
            currentChannelId,
        });
        const roles = new Set<string>();

        for (const member of channelMembers) {
            for (const role of member.roles.split(' ')) {
                roles.add(role);
            }
        }
        if (roles.size > 0) {
            dispatch(loadRolesIfNeeded(roles));
        }

        return {data: channelMembers};
    };
}

export function getChannelMembers(
    channelId: string,
    page = 0,
    perPage: number = General.CHANNELS_CHUNK_SIZE,
): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let channelMembers: ChannelMembership[];

        try {
            channelMembers = await Client4.getChannelMembers(channelId, page, perPage);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        const userIds = channelMembers.map((cm) => cm.user_id);

        dispatch(fetchUsersByIdsDebounced(userIds));

        dispatch({
            type: ChannelTypes.RECEIVED_CHANNEL_MEMBERS,
            data: channelMembers,
        });

        return {data: channelMembers};
    };
}

export function joinChannel(userId: string, teamId: string, channelId: string, channelName: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        if (!channelId && !channelName) {
            return {data: null};
        }

        let member: ChannelMembership | undefined | null;
        let channel: Channel;
        try {
            if (channelId) {
                const [addToChannelResult, getChannelResult] = await Promise.all([
                    Client4.addToChannel(userId, channelId),
                    Client4.getChannel(channelId),
                ]);

                member = addToChannelResult;
                channel = getChannelResult;
            } else {
                channel = await Client4.getChannelByName(teamId, channelName, true);
                if (channel.type === General.GM_CHANNEL || channel.type === General.DM_CHANNEL) {
                    member = await Client4.getChannelMember(channel.id, userId);
                } else {
                    member = await Client4.addToChannel(userId, channel.id);
                }
            }
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        Client4.trackEvent('action', 'action_channels_join', {channel_id: channelId});

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNEL,
                    data: channel,
                },
                {
                    type: ChannelTypes.RECEIVED_MY_CHANNEL_MEMBER,
                    data: member,
                },
            ]),
        );

        dispatch(addChannelToInitialCategory({channel, setOnServer: false}));

        if (member) {
            dispatch(loadRolesIfNeeded(member.roles.split(' ')));
            dispatch(readOnlyChannelsSlice.actions.removeChannel(channel.id));
        }

        return {data: {channel, member}};
    };
}

export function deleteChannel(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let state = getState();
        const viewArchivedChannels = state.entities.general.config.ExperimentalViewArchivedChannels === 'true';

        try {
            await Client4.deleteChannel(channelId);
        } catch (error) {
            if (error instanceof ClientError && error.server_error_id === ServerErrorIds.INSUFFICIENT_PERMISSIONS) {
                dispatch(openInsufficientPermissionsModal('ARCHIVE_CHANNEL'));
            } else {
                forceLogoutIfNecessary(error, dispatch, getState);
            }

            return {error};
        }

        state = getState();
        const {currentChannelId} = state.entities.channels;
        if (channelId === currentChannelId && !viewArchivedChannels) {
            const teamId = getCurrentTeamId(state);
            const channelsInTeam = getChannelsNameMapInTeam(state, teamId);
            const channel = Utils.getChannelByName(channelsInTeam, getRedirectChannelNameForTeam(state, teamId));
            if (channel && channel.id) {
                dispatch(selectChannel(channel.id));
            }
        }

        dispatch({type: ChannelTypes.DELETE_CHANNEL_SUCCESS, data: {id: channelId, viewArchivedChannels}});

        return {data: true};
    };
}

export function unarchiveChannel(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        try {
            await Client4.unarchiveChannel(channelId);
        } catch (error) {
            if (error instanceof ClientError && error.server_error_id === ServerErrorIds.INSUFFICIENT_PERMISSIONS) {
                dispatch(openInsufficientPermissionsModal('UNARCHIVE_CHANNEL'));
            } else {
                forceLogoutIfNecessary(error, dispatch, getState);
            }

            return {error};
        }

        const state = getState();
        const config = getConfig(state);
        const viewArchivedChannels = config.ExperimentalViewArchivedChannels === 'true';
        dispatch({type: ChannelTypes.UNARCHIVED_CHANNEL_SUCCESS, data: {id: channelId, viewArchivedChannels}});

        return {data: true};
    };
}

export function viewChannel(channelId: string, prevChannelId = ''): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const {currentUserId} = getState().entities.users;

        const {myPreferences} = getState().entities.preferences;
        const viewTimePref = myPreferences[`${Preferences.CATEGORY_CHANNEL_APPROXIMATE_VIEW_TIME}--${channelId}`];
        const viewTime = viewTimePref ? parseInt(viewTimePref.value!, 10) : 0;
        const prevChanManuallyUnread = isManuallyUnread(getState(), prevChannelId);

        // eslint-disable-next-line no-mixed-operators
        if (viewTime < new Date().getTime() - 3 * 60 * 60 * 1000) {
            const preferences = [
                {
                    user_id: currentUserId,
                    category: Preferences.CATEGORY_CHANNEL_APPROXIMATE_VIEW_TIME,
                    name: channelId,
                    value: new Date().getTime().toString(),
                },
            ];
            savePreferences(currentUserId, preferences)(dispatch);
        }

        try {
            await Client4.viewMyChannel(channelId, prevChanManuallyUnread ? '' : prevChannelId);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);

            return {error};
        }

        const actions: AnyAction[] = [];

        const {myMembers} = getState().entities.channels;
        const member = myMembers[channelId];
        if (member) {
            if (isManuallyUnread(getState(), channelId)) {
                actions.push({
                    type: ChannelTypes.REMOVE_MANUALLY_UNREAD,
                    data: {channelId},
                });
            }
            actions.push({
                type: ChannelTypes.VIEW_CHANNEL,
                data: {channelId: member.channel_id},
            });
            dispatch(loadRolesIfNeeded(member.roles.split(' ')));
        }

        const prevMember = myMembers[prevChannelId];
        if (!prevChanManuallyUnread && prevMember) {
            actions.push({
                type: ChannelTypes.VIEW_CHANNEL,
                data: {channelId: prevChannelId},
            });
            dispatch(loadRolesIfNeeded(prevMember.roles.split(' ')));
        }

        dispatch(batchActions(actions));

        return {data: true};
    };
}

export function markChannelAsViewed(channelId: string, prevChannelId?: string): ActionFunc {
    return (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const actions = actionsToMarkChannelAsViewed(getState, channelId, prevChannelId);

        return dispatch(batchActions(actions));
    };
}

export function actionsToMarkChannelAsViewed(getState: GetStateFunc, channelId: string, prevChannelId = '') {
    const actions: AnyAction[] = [];

    const state = getState();
    const {myMembers} = state.entities.channels;

    const member = myMembers[channelId];
    if (member) {
        actions.push({
            type: ChannelTypes.VIEW_CHANNEL,
            data: {channelId: member.channel_id},
        });

        if (isManuallyUnread(state, channelId)) {
            actions.push({
                type: ChannelTypes.REMOVE_MANUALLY_UNREAD,
                data: {channelId},
            });
        }
    }

    const prevMember = myMembers[prevChannelId];
    if (prevMember && !isManuallyUnread(state, prevChannelId)) {
        actions.push({
            type: ChannelTypes.VIEW_CHANNEL,
            data: {channelId: prevMember.channel_id},
        });
    }

    return actions;
}

export function getChannels(teamId: string, page = 0, perPage: number = General.CHANNELS_CHUNK_SIZE): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.GET_CHANNELS_REQUEST, data: null});

        let channels;
        try {
            channels = await Client4.getChannels(teamId, page, perPage);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.GET_CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNELS,
                    teamId,
                    data: channels,
                },
                {
                    type: ChannelTypes.GET_CHANNELS_SUCCESS,
                },
            ]),
        );

        return {data: channels};
    };
}

export const getChannelsByNames = (teamId: string, names: string[]) => {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let res;

        try {
            res = await Client4.getChannelsByNames(teamId, names);
        } catch (error) {
            forceLogoutIfNecessary(error as Client4Error, dispatch, getState);
            dispatch({type: ChannelTypes.GET_CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch(
            {
                type: ChannelTypes.RECEIVED_CHANNELS,
                teamId,
                data: res,
            },
        );

        return {data: res};
    };
};

export function getArchivedChannels(
    teamId: string,
    page = 0,
    perPage: number = General.CHANNELS_CHUNK_SIZE,
): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let channels;
        try {
            channels = await Client4.getArchivedChannels(teamId, page, perPage);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        dispatch({
            type: ChannelTypes.RECEIVED_CHANNELS,
            teamId,
            data: channels,
        });

        return {data: channels};
    };
}

export function getAllChannelsWithCount(
    page = 0,
    perPage: number = General.CHANNELS_CHUNK_SIZE,
    notAssociatedToGroup = '',
    excludeDefaultChannels = false,
    includeDeleted = false,
    excludePolicyConstrained = false,
): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.GET_ALL_CHANNELS_REQUEST, data: null});

        let payload;
        try {
            payload = (await Client4.getAllChannels(
                page,
                perPage,
                notAssociatedToGroup,
                excludeDefaultChannels,
                true,
                includeDeleted,
                excludePolicyConstrained,
            )) as ChannelsWithTotalCount;
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.GET_ALL_CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_ALL_CHANNELS,
                    data: payload.channels,
                },
                {
                    type: ChannelTypes.GET_ALL_CHANNELS_SUCCESS,
                },
                {
                    type: ChannelTypes.RECEIVED_TOTAL_CHANNEL_COUNT,
                    data: payload.total_count,
                },
            ]),
        );

        return {data: payload};
    };
}

export function getAllChannels(
    page = 0,
    perPage: number = General.CHANNELS_CHUNK_SIZE,
    notAssociatedToGroup = '',
    excludeDefaultChannels = false,
    excludePolicyConstrained = false,
): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.GET_ALL_CHANNELS_REQUEST, data: null});

        let channels;
        try {
            channels = await Client4.getAllChannels(
                page,
                perPage,
                notAssociatedToGroup,
                excludeDefaultChannels,
                false,
                false,
                excludePolicyConstrained,
            );
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.GET_ALL_CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_ALL_CHANNELS,
                    data: channels,
                },
                {
                    type: ChannelTypes.GET_ALL_CHANNELS_SUCCESS,
                },
            ]),
        );

        return {data: channels};
    };
}

export function autocompleteChannels(teamId: string, term: string, params?: {archived?: boolean; boost?: boolean}): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.GET_CHANNELS_REQUEST, data: null});

        let channels;
        try {
            channels = await Client4.autocompleteChannels(teamId, term, params);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.GET_CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNELS,
                    teamId,
                    data: channels,
                },
                {
                    type: ChannelTypes.GET_CHANNELS_SUCCESS,
                },
            ]),
        );

        return {data: channels};
    };
}

export function autocompleteChannelsForSearch(teamId: string, term: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.GET_CHANNELS_REQUEST, data: null});

        let channels;
        try {
            channels = await Client4.autocompleteChannelsForSearch(teamId, term);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.GET_CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNELS,
                    teamId,
                    data: channels,
                },
                {
                    type: ChannelTypes.GET_CHANNELS_SUCCESS,
                },
            ]),
        );

        return {data: channels};
    };
}

export function searchChannels(teamId: string, term: string, archived?: boolean): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.GET_CHANNELS_REQUEST, data: null});

        let channels;
        try {
            if (archived) {
                channels = await Client4.searchArchivedChannels(teamId, term);
            } else {
                channels = await Client4.searchChannels(teamId, term);
            }
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.GET_CHANNELS_FAILURE, error});
            return {error};
        }

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_CHANNELS,
                    teamId,
                    data: channels,
                },
                {
                    type: ChannelTypes.GET_CHANNELS_SUCCESS,
                },
            ]),
        );

        return {data: channels};
    };
}

export function searchAllChannels(term: string, opts: ChannelSearchOpts = {}): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        dispatch({type: ChannelTypes.GET_ALL_CHANNELS_REQUEST, data: null});

        let response;
        try {
            response = (await Client4.searchAllChannels(term, opts)) as ChannelsWithTotalCount;
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            dispatch({type: ChannelTypes.GET_ALL_CHANNELS_FAILURE, error});
            return {error};
        }

        const channels = response.channels || response;

        dispatch(
            batchActions([
                {
                    type: ChannelTypes.RECEIVED_ALL_CHANNELS,
                    data: channels,
                },
                {
                    type: ChannelTypes.GET_ALL_CHANNELS_SUCCESS,
                },
            ]),
        );

        return {data: response};
    };
}

export function searchGroupChannels(term: string, fetchOptions: Partial<Options> = {}): ThunkActionWithResult {
    return bindClientFunc({
        clientFunc: Client4.searchGroupChannels,
        params: [term, fetchOptions],
    });
}

export function getChannelStats(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let stat;
        try {
            stat = await Client4.getChannelStats(channelId);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        dispatch({
            type: ChannelTypes.RECEIVED_CHANNEL_STATS,
            data: stat,
        });

        return {data: stat};
    };
}

export function addChannelMember(channelId: string, userId: string, postRootId = ''): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        let member;
        try {
            member = await Client4.addToChannel(userId, channelId, postRootId);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        Client4.trackEvent('action', 'action_channels_add_member', {channel_id: channelId});

        dispatch(
            batchActions(
                [
                    {
                        type: UserTypes.RECEIVED_PROFILE_IN_CHANNEL,
                        data: {id: channelId, user_id: userId},
                    },
                    {
                        type: ChannelTypes.RECEIVED_CHANNEL_MEMBER,
                        data: member,
                    },
                    {
                        type: ChannelTypes.ADD_CHANNEL_MEMBER_SUCCESS,
                        id: channelId,
                    },
                ],
                'ADD_CHANNEL_MEMBER.BATCH',
            ),
        );

        return {data: member};
    };
}

export function addChannelMembers(channelId: string, userIds: string[], rootPostId?: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        try {
            const members = await Client4.addToChannelMany(userIds, channelId, rootPostId);

            const membersAdded = members.filter((value) => !value.error);
            dispatch({
                type: UserTypes.RECEIVED_PROFILES_LIST_IN_CHANNEL,
                id: channelId,
                data: membersAdded.map(({member}) => member.user_id),
            });
            dispatch({
                type: ChannelTypes.RECEIVED_CHANNEL_MEMBERS,
                data: membersAdded,
            });
            dispatch({
                type: ChannelTypes.ADD_CHANNEL_MEMBERS_SUCCESS,
                id: channelId,
                data: membersAdded.length,
            });
            return {data: {members}};
        } catch (error: unknown) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }
    };
}

export function removeChannelMember(channelId: string, userId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        try {
            await Client4.removeFromChannel(userId, channelId);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        Client4.trackEvent('action', 'action_channels_remove_member', {channel_id: channelId});

        dispatch(
            batchActions(
                [
                    {
                        type: UserTypes.RECEIVED_PROFILE_NOT_IN_CHANNEL,
                        data: {id: channelId, user_id: userId},
                    },
                    {
                        type: ChannelTypes.REMOVE_CHANNEL_MEMBER_SUCCESS,
                        id: channelId,
                    },
                ],
                'REMOVE_CHANNEL_MEMBER.BATCH',
            ),
        );

        return {data: true};
    };
}

export function updateChannelMemberRoles(channelId: string, userId: string, roles: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        try {
            await Client4.updateChannelMemberRoles(channelId, userId, roles);
        } catch (error: any) {
            forceLogoutIfNecessary(error, dispatch, getState);
            return {error};
        }

        const membersInChannel = getState().entities.channels.membersInChannel[channelId];
        if (membersInChannel && membersInChannel[userId]) {
            dispatch({
                type: ChannelTypes.RECEIVED_CHANNEL_MEMBER,
                data: {...membersInChannel[userId], roles},
            });
        }

        return {data: true};
    };
}

export function updateChannelHeader(channelId: string, header: string): ActionFunc {
    return async (dispatch: DispatchFunc) => {
        Client4.trackEvent('action', 'action_channels_update_header', {channel_id: channelId});

        dispatch({
            type: ChannelTypes.UPDATE_CHANNEL_HEADER,
            data: {
                channelId,
                header,
            },
        });

        return {data: true};
    };
}

export function updateChannelPurpose(channelId: string, purpose: string): ActionFunc {
    return async (dispatch: DispatchFunc) => {
        Client4.trackEvent('action', 'action_channels_update_purpose', {channel_id: channelId});

        dispatch({
            type: ChannelTypes.UPDATE_CHANNEL_PURPOSE,
            data: {
                channelId,
                purpose,
            },
        });

        return {data: true};
    };
}

export function markChannelAsRead(channelId: string, prevChannelId?: string, updateLastViewedAt = true): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const prevChanManuallyUnread = isManuallyUnread(state, prevChannelId);

        // Send channel last viewed at to the server
        if (updateLastViewedAt) {
            await dispatch(markChannelAsReadOnServer(channelId, prevChanManuallyUnread ? '' : prevChannelId));
        }

        const actions = actionsToMarkChannelAsRead(getState, channelId, prevChannelId);

        if (actions.length > 0) {
            dispatch(batchActions(actions));
        }

        return {data: true};
    };
}

export function markChannelAsReadOnServer(channelId: string, prevChannelId?: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        try {
            await Client4.viewMyChannel(channelId, prevChannelId);
        } catch (error) {
            const e = error as Client4Error;
            forceLogoutIfNecessary(e, dispatch, getState);
            return {error: e};
        }

        return {data: true};
    };
}

export function actionsToMarkChannelAsRead(getState: GetStateFunc, channelId: string, prevChannelId?: string) {
    const state = getState();
    const {channels, messageCounts, myMembers} = state.entities.channels;

    const prevChanManuallyUnread = isManuallyUnread(state, prevChannelId);

    // Update channel member objects to set all mentions and posts as viewed
    const channel = channels[channelId];
    const messageCount = messageCounts[channelId];
    const prevChannel = !prevChanManuallyUnread && prevChannelId ? channels[prevChannelId] : null; // May be null since prevChannelId is optional
    const prevMessageCount = !prevChanManuallyUnread && prevChannelId ? messageCounts[prevChannelId] : null;

    // Update team member objects to set mentions and posts in channel as viewed
    const channelMember = myMembers[channelId];
    const prevChannelMember = !prevChanManuallyUnread && prevChannelId ? myMembers[prevChannelId] : null; // May also be null

    const actions: AnyAction[] = [];

    if (channel && channelMember && messageCount) {
        const amount = messageCount.total - channelMember.msg_count;
        const amountRoot = messageCount.root - channelMember.msg_count_root;

        if (amount !== 0 || amountRoot !== 0) {
            actions.push({
                type: ChannelTypes.DECREMENT_UNREAD_MSG_COUNT,
                data: {
                    teamId: channel.team_id,
                    channelId,
                    amount,
                    amountRoot,
                },
            });
        }

        if (channelMember.mention_count !== 0 || channelMember.mention_count_root !== 0) {
            actions.push({
                type: ChannelTypes.DECREMENT_UNREAD_MENTION_COUNT,
                data: {
                    teamId: channel.team_id,
                    channelId,
                    amount: channelMember.mention_count,
                    amountRoot: channelMember.mention_count_root,
                },
            });
        }
    }

    if (channel && isManuallyUnread(getState(), channelId)) {
        actions.push({
            type: ChannelTypes.REMOVE_MANUALLY_UNREAD,
            data: {channelId},
        });
    }

    if (prevChannel && prevChannelMember && prevMessageCount) {
        const amount = prevMessageCount.total - prevChannelMember.msg_count;
        const amountRoot = prevMessageCount.root - prevChannelMember.msg_count_root;

        if (amount !== 0 || amountRoot !== 0) {
            actions.push({
                type: ChannelTypes.DECREMENT_UNREAD_MSG_COUNT,
                data: {
                    teamId: prevChannel.team_id,
                    channelId: prevChannelId,
                    amount,
                    amountRoot,
                },
            });
        }

        if (prevChannelMember.mention_count !== 0 || prevChannelMember.mention_count_root !== 0) {
            actions.push({
                type: ChannelTypes.DECREMENT_UNREAD_MENTION_COUNT,
                data: {
                    teamId: prevChannel.team_id,
                    channelId: prevChannelId,
                    amount: prevChannelMember.mention_count,
                    amountRoot: prevChannelMember.mention_count_root,
                },
            });
        }
    }

    return actions;
}

// Increments the number of posts in the channel by 1 and marks it as unread if necessary
export function markChannelAsUnread(
    teamId: string,
    channelId: string,
    mentions: string[],
    fetchedChannelMember = false,
    isRoot = false,
): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const {myMembers} = state.entities.channels;
        const {currentUserId} = state.entities.users;

        const actions: AnyAction[] = [
            {
                type: ChannelTypes.INCREMENT_UNREAD_MSG_COUNT,
                data: {
                    teamId,
                    channelId,
                    amount: 1,
                    amountRoot: isRoot ? 1 : 0,
                    onlyMentions:
                        myMembers[channelId] &&
                        myMembers[channelId].notify_props &&
                        myMembers[channelId].notify_props.mark_unread === MarkUnread.MENTION,
                    fetchedChannelMember,
                },
            },
        ];

        if (!fetchedChannelMember) {
            actions.push({
                type: ChannelTypes.INCREMENT_TOTAL_MSG_COUNT,
                data: {
                    channelId,
                    amountRoot: isRoot ? 1 : 0,
                    amount: 1,
                },
            });
        }

        if (mentions && mentions.indexOf(currentUserId) !== -1) {
            actions.push({
                type: ChannelTypes.INCREMENT_UNREAD_MENTION_COUNT,
                data: {
                    teamId,
                    channelId,
                    amountRoot: isRoot ? 1 : 0,
                    amount: 1,
                    fetchedChannelMember,
                },
            });
        }

        dispatch(batchActions(actions));

        return {data: true};
    };
}

export function actionsToMarkChannelAsUnread(
    getState: GetStateFunc,
    teamId: string,
    channelId: string,
    mentions: string[],
    fetchedChannelMember = false,
    isRoot = false,
) {
    const state = getState();
    const {myMembers} = state.entities.channels;
    const {currentUserId} = state.entities.users;

    const actions: AnyAction[] = [
        {
            type: ChannelTypes.INCREMENT_UNREAD_MSG_COUNT,
            data: {
                teamId,
                channelId,
                amount: 1,
                amountRoot: isRoot ? 1 : 0,
                onlyMentions:
                    myMembers[channelId] &&
                    myMembers[channelId].notify_props &&
                    myMembers[channelId].notify_props.mark_unread === MarkUnread.MENTION,
                fetchedChannelMember,
            },
        },
    ];

    if (!fetchedChannelMember) {
        actions.push({
            type: ChannelTypes.INCREMENT_TOTAL_MSG_COUNT,
            data: {
                channelId,
                amountRoot: isRoot ? 1 : 0,
                amount: 1,
            },
        });
    }

    if (mentions && mentions.indexOf(currentUserId) !== -1) {
        actions.push({
            type: ChannelTypes.INCREMENT_UNREAD_MENTION_COUNT,
            data: {
                teamId,
                channelId,
                amountRoot: isRoot ? 1 : 0,
                amount: 1,
                fetchedChannelMember,
            },
        });
    }

    return actions;
}

export function getChannelMembersByIds(channelId: string, userIds: string[]) {
    return bindClientFunc({
        clientFunc: Client4.getChannelMembersByIds,
        onSuccess: ChannelTypes.RECEIVED_CHANNEL_MEMBERS,
        params: [channelId, userIds],
    });
}

export function getChannelMember(channelId: string, userId: string) {
    return bindClientFunc({
        clientFunc: Client4.getChannelMember,
        onSuccess: ChannelTypes.RECEIVED_CHANNEL_MEMBER,
        params: [channelId, userId],
    });
}

export function getMyChannelMember(channelId: string) {
    return bindClientFunc({
        clientFunc: Client4.getMyChannelMember,
        onSuccess: ChannelTypes.RECEIVED_MY_CHANNEL_MEMBER,
        params: [channelId],
    });
}

export function loadMyChannelMemberAndRole(channelId: string) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const result = (await getMyChannelMember(channelId)(dispatch, getState)) as ActionResult;
        const roles = result.data?.roles.split(' ');
        if (roles && roles.length > 0) {
            dispatch(loadRolesIfNeeded(roles));
        }
        return {data: true};
    };
}

// favoriteChannel moves the provided channel into the current team's Favorites category.
export function favoriteChannel(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const channel = getChannelSelector(state, channelId);
        const category = getCategoryInTeamByType(
            state,
            channel.team_id || getCurrentTeamId(state),
            CategoryTypes.FAVORITES,
        );

        Client4.trackEvent('action', 'action_channels_favorite');

        if (!category) {
            return {data: false};
        }

        return dispatch(addChannelToChannelCategory({category, channel}));
    };
}

// unfavoriteChannel moves the provided channel into the current team's Channels/DMs category.
export function unfavoriteChannel(channelId: string): ActionFunc {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState();
        const channel = getChannelSelector(state, channelId);
        const category = getCategoryInTeamByType(
            state,
            channel.team_id || getCurrentTeamId(state),
            channel.type === General.DM_CHANNEL || channel.type === General.GM_CHANNEL ? CategoryTypes.DIRECT_MESSAGES : CategoryTypes.CHANNELS,
        );

        Client4.trackEvent('action', 'action_channels_unfavorite');

        if (!category) {
            return {data: false};
        }

        return dispatch(addChannelToChannelCategory({category, channel}));
    };
}

export function updateChannelScheme(channelId: string, schemeId: string) {
    return bindClientFunc({
        clientFunc: async () => {
            await Client4.updateChannelScheme(channelId, schemeId);
            return {channelId, schemeId};
        },
        onSuccess: ChannelTypes.UPDATED_CHANNEL_SCHEME,
    });
}

export function updateChannelMemberSchemeRoles(
    channelId: string,
    userId: string,
    isSchemeUser: boolean,
    isSchemeAdmin: boolean,
) {
    return bindClientFunc({
        clientFunc: async () => {
            await Client4.updateChannelMemberSchemeRoles(channelId, userId, isSchemeUser, isSchemeAdmin);
            return {channelId, userId, isSchemeUser, isSchemeAdmin};
        },
        onSuccess: ChannelTypes.UPDATED_CHANNEL_MEMBER_SCHEME_ROLES,
    });
}

export function membersMinusGroupMembers(
    channelID: string,
    groupIDs: string[],
    page = 0,
    perPage: number = General.PROFILE_CHUNK_SIZE,
): ActionFunc {
    return bindClientFunc({
        clientFunc: Client4.channelMembersMinusGroupMembers,
        onSuccess: ChannelTypes.RECEIVED_CHANNEL_MEMBERS_MINUS_GROUP_MEMBERS,
        params: [channelID, groupIDs, page, perPage],
    });
}

export function getChannelModerations(channelId: string): ActionFunc {
    return bindClientFunc({
        clientFunc: async () => {
            const moderations = await Client4.getChannelModerations(channelId);
            return {channelId, moderations};
        },
        onSuccess: ChannelTypes.RECEIVED_CHANNEL_MODERATIONS,
        params: [channelId],
    });
}

export function patchChannelModerations(channelId: string, patch: ChannelModerationPatch[]): ActionFunc {
    return bindClientFunc({
        clientFunc: async () => {
            const moderations = await Client4.patchChannelModerations(channelId, patch);
            return {channelId, moderations};
        },
        onSuccess: ChannelTypes.RECEIVED_CHANNEL_MODERATIONS,
        params: [channelId],
    });
}

export function getPrimaryChannelIfNeeded(teamId: Team['id']) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const team = getTeamById(getState(), teamId);

        if (team?.primary_channel_id) {
            return;
        }

        try {
            const channel = await Client4.getTeamPrimaryChannel(teamId);
            dispatch({type: ChannelTypes.RECEIVED_PRIMARY_CHANNEL, data: {teamId, channel}});
        } catch (error) {
            return {error};
        }
    };
}

export default {
    createChannel,
    createDirectChannel,
    updateChannel,
    patchChannel,
    updateChannelNotifyProps,
    getChannel,
    fetchMyChannelsAndMembers,
    getMyChannelMembers,
    getChannelTimezones,
    getChannelMembersByIds,
    joinChannel,
    deleteChannel,
    unarchiveChannel,
    viewChannel,
    markChannelAsViewed,
    getChannels,
    autocompleteChannels,
    autocompleteChannelsForSearch,
    searchChannels,
    searchGroupChannels,
    getChannelStats,
    addChannelMember,
    removeChannelMember,
    updateChannelHeader,
    updateChannelPurpose,
    markChannelAsRead,
    markChannelAsUnread,
    favoriteChannel,
    unfavoriteChannel,
    membersMinusGroupMembers,
    getChannelModerations,
};
